import * as THREE from 'three'

/** 与 `three.js-187/examples/test2.html` → `enhanceMaterials` 一致 */
export const CLASSIC_PRODUCT_MATERIAL_ENV_MAP_INTENSITY = 1.15

const DEFAULT_TRANSMISSION_THICKNESS = 0.5
const DEFAULT_TRANSMISSION_IOR = 1.5

/**
 * 压缩 glTF 合并材质后，浅色外壳常被误标为 `metalness = 1`（缺省值），
 * 在 IBL 下背面会发黑；与 test2 观感对齐时须还原为电介质。
 */
const DIELECTRIC_SHELL_METALNESS_MAX = 0.05
const DIELECTRIC_SHELL_LUMINANCE_MIN = 0.52

/**
 * 「实体金属」的最低粗糙度：低于这个值会让 PBR 退化为镜面，把环境锐利地映到机身上，
 * 与产品摄影的柔和金属反光观感冲突（典型：`macbook15-air-compressed.glb` 中
 * `roughness = 0 / metalness ≈ 1` 的外壳材质）。
 *
 * 仅当材质未提供 `metalnessMap` / `roughnessMap` 时认为参数完全由 GLB factor 决定，
 * 才把 roughness 抬到此地板值；有贴图驱动的金属保持作者意图。
 */
const SOLID_METAL_ROUGHNESS_FLOOR = 0.35

/** 触发 {@link SOLID_METAL_ROUGHNESS_FLOOR} 抬升的最低 metalness 阈值（包含半金属） */
const SOLID_METAL_FLOOR_METALNESS_MIN = 0.5

/** 仅当 roughness 低于这个值才视为「镜面金属」，再抬到 {@link SOLID_METAL_ROUGHNESS_FLOOR} */
const SOLID_METAL_FLOOR_TRIGGER_ROUGHNESS = 0.1

export type EnhanceProductMaterialsOptions = {
  /** 跳过这些网格（例如屏幕：用户贴图自有处理流程，避免重复打 patch） */
  skipMeshes?: ReadonlySet<THREE.Mesh>
}

/**
 * @param color - 材质基色
 */
function srgbLuminance(color: THREE.Color): number {
  return 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b
}

/**
 * 压缩 glTF 经常把「浅色塑料 / 玻璃外壳」错误地写成 `metalness = 1`（导出端缺省值）；
 * 这种材质在 IBL 下背面会发黑。这里在确认材质没有 `metalnessMap`（不是贴图驱动）时
 * 还原为常规电介质（`metalness = 0.05`）。同时如果作者还把 `roughness` 也写成 0，会
 * 顺手抬到 1，避免变成镜面玻璃；有真实粗糙度时尊重作者数值。
 *
 * @param material - 标准/物理材质
 */
function fixMisTaggedDielectricShell(material: THREE.MeshStandardMaterial): void {
  if (material.metalnessMap) return
  if (material.metalness < 1 - 1e-4) return
  if (srgbLuminance(material.color) < DIELECTRIC_SHELL_LUMINANCE_MIN) return
  material.metalness = DIELECTRIC_SHELL_METALNESS_MAX
  if (material.roughness < 0.04) {
    material.roughness = 1
  }
}

/**
 * 「镜面金属」软化：部分压缩 glTF（典型 `macbook15-air-compressed.glb`）把抛光金属
 * 外壳直接导出为 `metalness ≈ 1 / roughness = 0`，会把 RoomEnvironment 锐利映到机身。
 * 仅在没有 metalnessMap / roughnessMap 时介入（贴图驱动的材质保持作者意图）。
 *
 * @param material - 标准/物理材质
 */
function softenSolidMetalMirror(material: THREE.MeshStandardMaterial): void {
  if (material.metalnessMap || material.roughnessMap) return
  if (material.metalness < SOLID_METAL_FLOOR_METALNESS_MIN) return
  if (material.roughness >= SOLID_METAL_FLOOR_TRIGGER_ROUGHNESS) return
  material.roughness = SOLID_METAL_ROUGHNESS_FLOOR
}

/**
 * 物理材质的透光默认值（仅在作者未指定时填默认，避免 ior=0 / thickness=0 失真）。
 *
 * @param material - 物理材质
 */
function ensureTransmissionDefaults(material: THREE.MeshPhysicalMaterial): void {
  if (material.transmission <= 0) return
  if (!material.thickness) material.thickness = DEFAULT_TRANSMISSION_THICKNESS
  if (!material.ior) material.ior = DEFAULT_TRANSMISSION_IOR
}

/**
 * 单张 PBR 材质：`envMapIntensity` + 误标金属度的浅色外壳 + 镜面金属软化 + 透光参数。
 *
 * @param material - 标准/物理材质
 */
export function enhanceProductMaterial(material: THREE.Material): void {
  if (
    !(material instanceof THREE.MeshStandardMaterial) &&
    !(material instanceof THREE.MeshPhysicalMaterial)
  ) {
    return
  }

  material.envMapIntensity = CLASSIC_PRODUCT_MATERIAL_ENV_MAP_INTENSITY
  fixMisTaggedDielectricShell(material)
  softenSolidMetalMirror(material)
  if (material instanceof THREE.MeshPhysicalMaterial) {
    ensureTransmissionDefaults(material)
  }

  material.needsUpdate = true
}

/**
 * 对子树所有网格的标准/物理材质执行 {@link enhanceProductMaterial}（对齐 `test2.html` → `enhanceMaterials`）。
 *
 * @param root - glTF 根或图层子树
 * @param options - 可选：跳过指定网格
 */
export function enhanceProductMaterialsOnTree(
  root: THREE.Object3D,
  options?: EnhanceProductMaterialsOptions,
): void {
  const skip = options?.skipMeshes
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return
    if (skip?.has(child)) return
    const materials = Array.isArray(child.material) ? child.material : [child.material]
    for (const material of materials) {
      enhanceProductMaterial(material)
    }
  })
}

/**
 * 还原 {@link enhanceProductMaterialsOnTree} 的 `envMapIntensity` 改动（用于卸载或切换观感策略）。
 *
 * @param root - glTF 根或图层子树
 */
export function resetProductMaterialsOnTree(root: THREE.Object3D): void {
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return
    const materials = Array.isArray(child.material) ? child.material : [child.material]
    for (const material of materials) {
      if (!(material instanceof THREE.MeshStandardMaterial)) continue
      material.envMapIntensity = 1
      material.needsUpdate = true
    }
  })
}
